/* eslint-disable camelcase */
/* eslint-disable no-throw-literal */
// @flow
import { push } from 'react-router-redux';
import { setUser as setBugsnagUser } from '../helpers/Bugsnag';
import { makeActionCreator, reduce } from '../helpers/utils';
import { actions as googleActions } from '../middleware/googleMiddleware';
import { actions as facebookActions } from '../middleware/facebookMiddleware';
import ApiClient from '../api/Api';
// eslint-disable-next-line import/no-cycle
import type { ClientAction } from './consents';
// import { getConsents } from './consents';

const debug = require('debug')('jp:reducers:auth');

const LOAD = 'auth/LOAD';
export const LOAD_SUCCESS = 'auth/LOAD_SUCCESS';
const LOAD_FAIL = 'auth/LOAD_FAIL';
const LOGIN = 'auth/LOGIN';
export const LOGIN_SUCCESS = 'auth/LOGIN_SUCCESS';
const LOGIN_FAIL = 'auth/LOGIN_FAIL';
const LOGOUT = 'auth/LOGOUT';
const LOGOUT_SUCCESS = 'auth/LOGOUT_SUCCESS';
const LOGOUT_FAIL = 'auth/LOGOUT_FAIL';
const REGISTER = 'auth/REGISTER';
const REGISTER_SUCCESS = 'auth/REGISTER_SUCCESS';
const REGISTER_FAIL = 'auth/REGISTER_FAIL';
const RESET_PASSWORD = 'auth/RESET_PASSWORD';
const RESET_PASSWORD_SUCCESS = 'auth/RESET_PASSWORD_SUCCESS';
const RESET_PASSWORD_FAIL = 'auth/RESET_PASSWORD_FAIL';
const GOOGLE_LOGIN = 'auth/GOOGLE_LOGIN';
const GOOGLE_LOGIN_SUCCESS = 'auth/GOOGLE_LOGIN_SUCCESS';
const GOOGLE_LOGIN_FAIL = 'auth/GOOGLE_LOGIN_FAIL';
const FACEBOOK_LOGIN = 'auth/FACEBOOK_LOGIN';
const FACEBOOK_LOGIN_SUCCESS = 'auth/FACEBOOK_LOGIN_SUCCESS';
const FACEBOOK_LOGIN_FAIL = 'auth/FACEBOOK_LOGIN_FAIL';
const UPDATE_REG_FORM = 'auth/UPDATE_REG_FORM';

type Action =
  | {| type: typeof LOAD |}
  | {| type: typeof LOAD_SUCCESS |}
  | {| type: typeof LOAD_FAIL |}
  | {| type: typeof LOGIN |}
  | {| type: typeof LOGIN_SUCCESS, payload: Object |}
  | {| type: typeof LOGOUT |}
  | {| type: typeof LOGOUT_SUCCESS |}
  | {| type: typeof LOGOUT_FAIL |}
  | {| type: typeof REGISTER |}
  | {| type: typeof REGISTER_FAIL |}
  | {| type: typeof REGISTER_SUCCESS, payload: Object |}
  | {| type: typeof RESET_PASSWORD |}
  | {| type: typeof GOOGLE_LOGIN |}
  | {| type: typeof GOOGLE_LOGIN_FAIL |}
  | {| type: typeof GOOGLE_LOGIN_SUCCESS, payload: Object |}
  | {| type: typeof FACEBOOK_LOGIN |}
  | {| type: typeof FACEBOOK_LOGIN_FAIL |}
  | {| type: typeof FACEBOOK_LOGIN_SUCCESS, payload: Object |}
  | {| type: typeof UPDATE_REG_FORM |}
  | {|
      types: Array<string>,
      params?: Object,
      promise: ApiClient => Promise<{}>
    |};

export type User = {
  id: number,
  email: string,
  hasSeasonTicket: boolean,
  isSpaceOwner: boolean,
  isSuperUser: boolean
};

export type State = {|
  loaded: boolean,
  loading: boolean,
  loggingIn: boolean,
  loggingOut: boolean,
  user: ?User,
  loginError: ?string,
  registerError: ?string,
  logoutError: ?string,
  resetPasswordMessage: ?string,
  resetPasswordError: ?string,
  deletedAccount: boolean,
  facebookLoading: boolean,
  googleLoading: boolean,
  newUser: boolean,
  registration: {
    isDirty: boolean,
    registerValid: boolean,
    firstName: string,
    lastName: string,
    password: string,
    email: string,
    phone: string
  }
|};

const initialState: State = {
  loaded: false,
  loading: false,
  loggingIn: false,
  loggingOut: false,
  user: null,
  loginError: null,
  registerError: null,
  logoutError: null,
  resetPasswordMessage: null,
  deletedAccount: false,
  facebookLoading: false,
  googleLoading: false,
  newUser: false,
  resetPasswordError: null,
  registration: {
    isDirty: false,
    registerValid: false,
    firstName: '',
    lastName: '',
    password: '',
    email: '',
    phone: '',
    countryCode: '44'
  }
};

const phoneResetMessage =
  'If your phone number is registered with JustPark, we’ve sent you instructions on how to reset your password via SMS. If you don’t receive this, please check you’ve typed your number correctly.';

const emailResetMessage =
  'If your email address is registered with JustPark, we’ve sent you instructions on how to reset your password via email. If you don’t receive this, please check you’ve typed your email correctly.';

const authIsLoaded = state => state.auth && state.auth.loaded;
const actionContainsUser = action =>
  action.payload.data && 'id' in action.payload.data;
const getUserFromAction = (action: Action) => {
  if (action.payload && action.payload.data) {
    return action.payload.data;
  }
  throw new Error('No user in getUserFromAction');
};

export const validName = (n: string) =>
  n.length > 0 && /[\w\d\s]+/i.test(n) ? '' : 'Please enter a valid name';

export const validEmail = (e: string) =>
  /[^@]+@[^.]+\.\w+/i.test(e) ? '' : 'Please enter a valid email address';

export const validPassword = (p: string) =>
  p.length >= 8 ? '' : 'Password must be at least 8 characters';

export const validPhoneNumber = (p: string) =>
  /\s*(?:[\d]\s*){7,}/i.test(p) ? '' : 'Please enter a valid phone number';

const validRegister = ({ nameError, passwordError, emailError }) => {
  debug(
    'validRegister (nameError, passwordError, emailError)',
    nameError,
    passwordError,
    emailError
  );
  return !nameError && !passwordError && !emailError;
};

const getErrors = ({ firstName, lastName, password, email }) => {
  const validationErrors = {
    nameError: validName(firstName) || validName(lastName),
    passwordError: validPassword(password),
    emailError: validEmail(email)
  };
  debug('getErrors', validationErrors);
  return validationErrors;
};

export default reduce(initialState, {
  [LOAD](state: State): State {
    debug(LOAD);
    return { ...state, loading: true };
  },
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, 'user in response', actionContainsUser(action));
    if (actionContainsUser(action)) {
      setBugsnagUser(getUserFromAction(action));
    }

    return {
      ...state,
      loading: false,
      loaded: actionContainsUser(action),
      user: getUserFromAction(action)
    };
  },
  [LOAD_FAIL](state: State): State {
    debug(LOAD_FAIL);
    return { ...state, loading: false, loaded: false };
  },
  [LOGIN](state: State): State {
    debug(LOGIN);
    return { ...state, loggingIn: true };
  },
  [LOGIN_SUCCESS](state: State, action): State {
    debug(LOGIN_SUCCESS, { userFromAction: getUserFromAction(action), action });
    setBugsnagUser(getUserFromAction(action));
    return { ...state, loggingIn: false, user: getUserFromAction(action) };
  },
  [RESET_PASSWORD](state: State): State {
    debug(RESET_PASSWORD);
    return {
      ...state
    };
  },
  [RESET_PASSWORD_FAIL](state: State, action: *): State {
    debug(RESET_PASSWORD_FAIL);
    return {
      ...state,
      resetPasswordMessage:
        action.payload.data.code === 10001
          ? emailResetMessage
          : phoneResetMessage
    };
  },
  [RESET_PASSWORD_SUCCESS](state: State, action: *): State {
    debug(RESET_PASSWORD_SUCCESS);
    return {
      ...state,
      resetPasswordMessage:
        action.payload.data.type === 'email'
          ? emailResetMessage
          : phoneResetMessage
    };
  },
  [FACEBOOK_LOGIN](state: State): State {
    debug(FACEBOOK_LOGIN);
    return {
      ...state,
      facebookLoading: true
    };
  },
  [GOOGLE_LOGIN](state: State): State {
    debug(GOOGLE_LOGIN);
    return {
      ...state,
      googleLoading: true
    };
  },
  [FACEBOOK_LOGIN_SUCCESS](state: State, action): State {
    debug(FACEBOOK_LOGIN_SUCCESS, getUserFromAction(action));
    setBugsnagUser(getUserFromAction(action));
    return { ...state, loggingIn: false, user: getUserFromAction(action) };
  },
  [GOOGLE_LOGIN_SUCCESS](state: State, action): State {
    debug(GOOGLE_LOGIN_SUCCESS, getUserFromAction(action));
    setBugsnagUser(getUserFromAction(action));
    return { ...state, loggingIn: false, user: getUserFromAction(action) };
  },
  [REGISTER](state: State): State {
    debug(REGISTER);
    return {
      ...state,
      loggingIn: true
    };
  },
  [REGISTER_SUCCESS](state: State, action): State {
    debug(REGISTER_SUCCESS, getUserFromAction(action));
    setBugsnagUser(getUserFromAction(action));
    return {
      ...state,
      loggingIn: false,
      user: getUserFromAction(action),
      newUser: true
    };
  },
  [REGISTER_FAIL](state: State, action): State {
    debug(REGISTER_FAIL, action.payload.data.title);
    return {
      ...state,
      loggingIn: false,
      user: null,
      registerError: action.payload.data.title,
      registration: { ...state.registration, showingErrors: true }
    };
  },
  [LOGIN_FAIL](state: State, action): State {
    debug(LOGIN_FAIL, action.payload.message);
    return {
      ...state,
      loggingIn: false,
      user: null,
      loginError: action.payload.message
    };
  },
  [LOGOUT](state: State): State {
    debug(LOGOUT);
    return { ...state, loggingOut: true };
  },
  [LOGOUT_SUCCESS](state: State): State {
    debug(LOGOUT_SUCCESS);
    return { ...state, loggingOut: false, user: null };
  },
  [LOGOUT_FAIL](state: State, action): State {
    debug(LOGOUT_FAIL, action.error);
    return { ...state, loggingOut: false, logoutError: action.error };
  },
  [UPDATE_REG_FORM](state: State, action): State {
    debug(UPDATE_REG_FORM, action);
    const { registration } = state;
    const newRegistration = {
      isDirty: true,
      ...registration,
      ...action.update
    };
    const errors = getErrors(newRegistration);
    return {
      ...state,
      registration: {
        ...newRegistration,
        registerValid: validRegister(errors),
        ...errors
      }
    };
  }
});

type GlobalState = { auth: State };

export function isLoaded(globalState: GlobalState) {
  return authIsLoaded(globalState);
}

export function hasPurchases(globalState: GlobalState) {
  return (
    globalState.auth &&
    globalState.auth.user &&
    globalState.auth.user.hasSeasonTicket
  );
}

export function load() {
  return (dispatch: Action => void, getState: () => GlobalState) => {
    if (!getState().auth.loading) {
      return dispatch({
        types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
        promise: client => client.me()
      });
    }
    return Promise.resolve(getState().auth.user);
  };
}

export function login(
  params: Object,
  email: string,
  password: string,
  recaptchaToken: string
) {
  return (dispatch: (ClientAction | Function) => Promise<?User>) =>
    dispatch({
      types: [LOGIN, LOGIN_SUCCESS, LOGIN_FAIL],
      params,
      promise: client =>
        client.login(params, email, password, recaptchaToken).then(user => ({
          ...user,
          space_owner: user.is_space_owner
        }))
    });
}

export function register(
  params: Object,
  email: string,
  name: string,
  password: string,
  token: string
): Action {
  return {
    types: [REGISTER, REGISTER_SUCCESS, REGISTER_FAIL],
    params,
    promise: client =>
      client.register(params, email, name, password, token).then(v4User =>
        client.me().then(me => ({
          ...me,
          space_owner: v4User.space_owner
        }))
      )
  };
}

export function resetPassword(email: string): Action {
  return {
    types: [RESET_PASSWORD, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAIL],
    promise: client => client.resetPassword(email)
  };
}

export function googleLogin(queryParams: Object) {
  return (dispatch: Action => Promise<{ access_token: string }>) => {
    dispatch(googleActions.login()).then(({ access_token }) => {
      dispatch({
        types: [GOOGLE_LOGIN, GOOGLE_LOGIN_SUCCESS, GOOGLE_LOGIN_FAIL],
        promise: (client: ApiClient) =>
          client.google(access_token, queryParams).then(() => client.me())
      });
    });
  };
}

export function facebookLogin(queryParams: Object) {
  return (dispatch: Action => Promise<{ accessToken: string }>) => {
    dispatch(facebookActions.login()).then(({ accessToken }) => {
      dispatch({
        types: [FACEBOOK_LOGIN, FACEBOOK_LOGIN_SUCCESS, FACEBOOK_LOGIN_FAIL],
        promise: client =>
          client.facebook(accessToken, queryParams).then(() => client.me())
      });
    });
  };
}

export function cancel() {
  return push('/');
}

export function logout(): Action {
  return {
    types: [LOGOUT, LOGOUT_SUCCESS, LOGOUT_FAIL],
    promise: client => client.logout()
  };
}

export const updateRegForm = makeActionCreator(UPDATE_REG_FORM, 'update');

export const analyticsEventSubscribers = {
  [LOGIN_SUCCESS]: (action: Action, analytics: Object) => {
    const user = getUserFromAction(action);
    analytics.track('action:log-in:successful', { method: 'EMAIL' });
    analytics.identify(user.id);
  },
  [LOGIN_FAIL]: (action: Action, analytics: Object) => {
    analytics.track('action:log-in:failed', { method: 'EMAIL' });
  },
  [REGISTER_SUCCESS]: (action: Action, analytics: Object) => {
    const user = getUserFromAction(action);
    analytics.track('action:sign-up:signup-successful', { method: 'EMAIL' });
    analytics.alias(user.id);
    analytics.identify(user.id);
  },
  [REGISTER_FAIL]: (action: Action, analytics: Object) => {
    analytics.track('action:sign-up:sign-up-failed', { method: 'EMAIL' });
  },
  [GOOGLE_LOGIN_SUCCESS]: (action: Action, analytics: Object) => {
    const user = getUserFromAction(action);
    analytics.track('action:log-in:successful', { method: 'GOOGLE' });

    if (user.newUser) {
      analytics.alias(user.id);
    }

    analytics.identify(user.id);
  },
  [GOOGLE_LOGIN_FAIL]: (action: Action, analytics: Object) => {
    analytics.track('action:log-in:failed', { method: 'GOOGLE' });
  },
  [FACEBOOK_LOGIN_SUCCESS]: (action: Action, analytics: Object) => {
    const user = getUserFromAction(action);
    analytics.track('action:log-in:successful', { method: 'FACEBOOK' });

    if (user.newUser) {
      analytics.alias(user.id);
    }

    analytics.identify(user.id);
  }
};
